import { ReactNode, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'Redux/store';
import { refuseOrder } from 'Redux/Order/reducer';
import { getOrder } from 'Redux/Order/reducer';
import { selectCurrentOrder } from 'Redux/Order/slice';
import {
  resetCurrentOrders,
  selectOrderLoading,
  selectOrderError,
} from 'Redux/Order/slice';
import { resetCurrentBids } from 'Redux/Bid/slice';
import { formatDate } from 'Shared/Utils';
import { Button, Modal, notification, Spin } from 'antd';
import {
  CloseCircleOutlined,
  ExclamationCircleFilled,
  LoadingOutlined,
} from '@ant-design/icons';
import styles from './Order.module.scss';

interface OrderProps {
  children?: ReactNode;
}

export const Order: React.FC<OrderProps> = ({ children }) => {
  const currentOrder = useSelector(selectCurrentOrder);
  const loading = useSelector(selectOrderLoading);
  const loadingError = useSelector(selectOrderError);
  const [orderIsExpired, setOrderIsExpired] = useState(false);
  const [orderIsRefused, setOrderIsRefused] = useState(false);
  const dispatch = useAppDispatch();
  const [modal, modalContextHolder] = Modal.useModal();
  const [notificationAPI, notificationContextHolder] =
    notification.useNotification();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetElement = event.target as HTMLElement;
      const tabindex = Number(targetElement.getAttribute('tabindex'));

      if (tabindex === -1) {
        Modal.destroyAll();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      dispatch(resetCurrentOrders());
      dispatch(resetCurrentBids());
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getOrderExpireState = (expirationTime?: string): boolean => {
    if (!expirationTime) {
      return false;
    }

    const expiresInMs = new Date(expirationTime).getTime();

    return expiresInMs <= new Date().getTime() + 1000 * 60;
  };

  useEffect(() => {
    let intervalID: ReturnType<typeof setInterval>;

    if (currentOrder) {
      if (
        currentOrder.order_status &&
        currentOrder.order_status.toLowerCase() === 'refused'
      ) {
        setOrderIsRefused(true);

        return;
      }

      setOrderIsExpired(getOrderExpireState(currentOrder.expires));

      intervalID = setInterval(() => {
        setOrderIsExpired(getOrderExpireState(currentOrder.expires));
      }, 1000 * 60);
    }

    return () => {
      clearInterval(intervalID);
    };
  }, [currentOrder]);

  const handleLoadRefusalConfirmation = () => {
    if (currentOrder?.id) {
      dispatch(refuseOrder({ order_id: currentOrder.id })).then((action) => {
        if (action.payload) {
          notificationAPI.success({
            message: 'Load has been refused',
            duration: 2,
          });

          dispatch(getOrder(currentOrder.id));
        } else {
          notificationAPI.error({
            message: "Internal error, load wasn't refused",
            duration: 4,
          });
        }
      });
    }
  };

  const showLoadRefusalModal = () => {
    modal.confirm({
      title: 'Would you like to refuse this load?',
      icon: <ExclamationCircleFilled />,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: handleLoadRefusalConfirmation,
    });
  };

  return (
    <div className={styles.order}>
      {modalContextHolder}
      {notificationContextHolder}
      {['active', 'checkout'].includes(
        String(currentOrder?.order_status).toLowerCase(),
      ) && (
        <Button
          className={styles.order__refuseBtn}
          htmlType="submit"
          type="primary"
          danger
          title="Refuse the load"
          onClick={showLoadRefusalModal}
        >
          {loading ? (
            <Spin
              indicator={
                <LoadingOutlined
                  className={styles.order__refuseBtn__spinner}
                  spin
                />
              }
            />
          ) : (
            <CloseCircleOutlined />
          )}
        </Button>
      )}

      <div className={styles.order__header}>
        <h2 className={styles.order__title}>
          Order&nbsp;
          {!currentOrder && loading
            ? 'is loading...'
            : !currentOrder && loadingError
            ? 'not found'
            : currentOrder?.id}
        </h2>

        <div className={styles.order__tags}>
          <div className={styles.order__tag}>
            RN#{currentOrder?.order_number}
          </div>
          <div
            className={`${styles.order__tag} ${
              orderIsExpired && styles.order__tag_warning
            }`}
          >
            {`Expire${orderIsExpired ? 'd' : 's'}
            ${formatDate({
              date: currentOrder?.expires,
              splitDateByDots: true,
              commaAfterDate: true,
              hour12: false,
            })}`}
          </div>
          {orderIsRefused && (
            <div className={`${styles.order__tag} ${styles.order__tag_danger}`}>
              Refused
            </div>
          )}
        </div>
      </div>

      {children}
    </div>
  );
};
